import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resetNavState } from "@/lib/navState";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onExpired = (e) => {
      setUser((prev) => {
        if (prev) toast.error(e.detail || "Your session has ended. Please sign in again.");
        return null;
      });
    };
    window.addEventListener("auth:expired", onExpired);
    window.addEventListener("auth:refresh", refresh);
    return () => { window.removeEventListener("auth:expired", onExpired); window.removeEventListener("auth:refresh", refresh); };
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      resetNavState();
      setUser(null);
    }
  }, []);

  const can = useCallback((cap) => !!user?.capabilities?.includes(cap), [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, refresh, logout, can }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
